import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, ArrowLeft, Star, CheckCircle2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { SupportChatOptions } from "./SupportChatOptions";
import { SupportFeedbackForm } from "./SupportFeedbackForm";
import { SupportChatMessages } from "./SupportChatMessages";

type ChatCategory = "feedback" | "functionality" | "navigation" | "human_support";
type ChatMessage = { role: "user" | "assistant"; content: string };
type ChatView = "options" | "feedback" | "chat";

export function SupportAgentChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ChatView>("options");
  const [category, setCategory] = useState<ChatCategory | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: replies = [] } = useQuery({
    queryKey: ["support-replies"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data } = await supabase
        .from("support_conversations")
        .select("id, feedback_text, admin_reply, admin_replied_at, reply_read_at")
        .eq("user_id", user.id)
        .not("admin_reply", "is", null)
        .order("admin_replied_at", { ascending: false })
        .limit(10);
      return data || [];
    },
    refetchInterval: 60_000,
  });
  const unreadIds = replies.filter((r: any) => !r.reply_read_at).map((r: any) => r.id);

  useEffect(() => {
    if (searchParams.get("support") === "open") {
      setIsOpen(true);
      searchParams.delete("support");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!isOpen || unreadIds.length === 0) return;
    supabase
      .from("support_conversations")
      .update({ reply_read_at: new Date().toISOString() })
      .in("id", unreadIds)
      .then(() => queryClient.invalidateQueries({ queryKey: ["support-replies"] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unreadIds.join(",")]);

  const handleSelectOption = (cat: ChatCategory) => {
    setCategory(cat);
    if (cat === "feedback") {
      setView("feedback");
    } else {
      setView("chat");
      const greetings: Record<string, string> = {
        functionality: "Olá! 👋 Sobre qual funcionalidade da plataforma você tem dúvida? Pode perguntar sobre Quiz, Desafios, Academy, Simulador de Entrevista, Comunidade, Vagas ou qualquer outra feature!",
        navigation: "Olá! 👋 O que você está procurando? Me diga o que quer fazer e eu te mostro o caminho exato na plataforma!",
        human_support: "Olá! 👋 Vou registrar sua solicitação para nossa equipe de suporte. Pode me descrever o que está acontecendo? Quanto mais detalhes, melhor poderemos te ajudar!",
      };
      setMessages([{ role: "assistant", content: greetings[cat] }]);
    }
  };

  const handleBack = () => {
    setView("options");
    setCategory(null);
    setMessages([]);
    setInput("");
    setConversationId(null);
  };

  const saveConversation = async (msgs: ChatMessage[], cat: ChatCategory) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (conversationId) {
      await supabase
        .from("support_conversations")
        .update({ messages: msgs as any, updated_at: new Date().toISOString() })
        .eq("id", conversationId);
    } else {
      const { data } = await supabase
        .from("support_conversations")
        .insert({
          user_id: user.id,
          category: cat,
          messages: msgs as any,
          status: cat === "human_support" ? "escalated" : "open",
        })
        .select("id")
        .single();
      if (data) setConversationId(data.id);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !category || isLoading) return;

    const userMsg: ChatMessage = { role: "user", content: input.trim() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);

    // Save to DB
    saveConversation(updatedMessages, category);

    // For human_support, just save and confirm
    if (category === "human_support" && updatedMessages.filter(m => m.role === "user").length >= 1) {
      // Still use AI to acknowledge and gather more info
    }

    // Stream AI response
    let assistantContent = "";
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-agent-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: updatedMessages,
            category,
          }),
        }
      );

      if (!resp.ok || !resp.body) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || "Erro na conexão");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2]?.role === "user") {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
                }
                return [...prev, { role: "assistant", content: assistantContent }];
              });
            }
          } catch { /* partial JSON */ }
        }
      }

      // Save final conversation with assistant response
      const finalMessages = [...updatedMessages, { role: "assistant" as const, content: assistantContent }];
      saveConversation(finalMessages, category);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Erro desconhecido";
      setMessages(prev => [...prev, { role: "assistant", content: `❌ ${errorMsg}. Tente novamente em instantes.` }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
          aria-label="Abrir suporte"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadIds.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-bold flex items-center justify-center animate-pulse">
              {unreadIds.length}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-4rem)] bg-background border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b bg-primary/5">
            {view !== "options" && (
              <button onClick={handleBack} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Agente de Suporte</h3>
              <p className="text-xs text-muted-foreground">Data Hire Club</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {view === "options" && (
              <div className="flex-1 overflow-y-auto">
                {replies.length > 0 && (
                  <div className="p-3 space-y-2 border-b">
                    <p className="text-xs font-semibold text-muted-foreground uppercase">Respostas do suporte</p>
                    {replies.map((r: any) => (
                      <div key={r.id} className="rounded-lg border bg-primary/5 p-3 space-y-1">
                        {r.feedback_text && (
                          <p className="text-xs text-muted-foreground line-clamp-2">Seu pedido: {r.feedback_text}</p>
                        )}
                        <p className="text-sm whitespace-pre-wrap flex gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span>{r.admin_reply}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground text-right">
                          {new Date(r.admin_replied_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <SupportChatOptions onSelect={handleSelectOption} />
              </div>
            )}
            {view === "feedback" && (
              <SupportFeedbackForm
                onSuccess={() => {
                  handleBack();
                  setIsOpen(false);
                }}
              />
            )}
            {view === "chat" && (
              <>
                <SupportChatMessages messages={messages} isLoading={isLoading} />
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Textarea
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      className="min-h-[40px] max-h-[80px] resize-none text-sm"
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      onClick={sendMessage}
                      disabled={!input.trim() || isLoading}
                      className="shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
